import { gql } from 'graphql-request';

import { 
  baseApi, 
  select, 
  insert,
  update,
  remove,
  afterInsert,
  afterUpdate,
  afterRemove,
  objectToStr,
  escapeBackslashes
} from './base';

import {
  constructionProjectType,
  constructionProjectNoFollowType,
  constructionProjectUpdateType,
  projectProfileType
} from '../graphqlTypes';

const projectApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getConstructionProjects: select(builder, 'project', constructionProjectType, false, {
      tags: ['ConstructionProject'],
      cacheKey: 'getConstructionProjects(undefined)',
      filter: (params) => {
        let str = '';
        str = (params.reviewerId ? `reviewer_id: { _eq: "${params.reviewerId}"}` : `reviewer_id: {}`)
          .concat(
            params.source
              ? `source: { _eq: "${params.source.charAt(0).toUpperCase() + params.source.slice(1)}"}`
              : `source: {}`
          )
          .concat(
            params.reviewStatus
              ? `review_status: { _eq: "${params.reviewStatus}"}`
              : `review_status: { _is_null: false}`
          )
          .concat(params.starred === 'Starred only' ? `project_profiles: { is_star: { _eq: true } }` : ``)
          .concat(params.state ? `state: { _eq: "${params.state}"}` : `state: { _is_null: false}`)
          .concat(params.projectStatus ? `,status: { _like: "${params.projectStatus}"}` : '')
          .concat(params.projectStage ? `,stage: { _like: "${params.projectStage}"}` : '')
          .concat(params.sector ? `,project_sectors: { name: { _like: "${params.sector}"}}` : '')
          .concat(params.greenBuilding ? `,green_building: {  _like: "${params.greenBuilding}"}` : '')
          .concat(params.id && params.id.match(/[Dd][Aa]/g) ? `,da_id: { _is_null: false}` : '')
          .concat(params.id && params.id.match(/[Tt]ender/g) ? `,tender_id: { _is_null: false}` : '')

          .concat(
            params.costMin && !params.costMax ? `,cost: { _gte: "${Number(params.costMin.replaceAll(',', ''))}"}` : ''
          )
          .concat(
            params.costMax && !params.costMin ? `,cost: { _lte: "${Number(params.costMax.replaceAll(',', ''))}"}` : ''
          )
          .concat(
            params.costMax && params.costMin
              ? `,cost: { _gte: "${Number(params.costMin.replaceAll(',', ''))}",  _lte: "${Number(
                  params.costMax.replaceAll(',', '')
                )}"}`
              : ''
          )

          .concat(
            params.estimatedCostMin && !params.estimatedCostMax
              ? `,estimated_cost: { _gte: "${Number(params.estimatedCostMin.replaceAll(',', ''))}"}`
              : ''
          )
          .concat(
            params.estimatedCostMax && !params.estimatedCostMin
              ? `,estimated_cost: { _lte: "${Number(params.estimatedCostMax.replaceAll(',', ''))}"}`
              : ''
          )
          .concat(
            params.estimatedCostMax && params.estimatedCostMin
              ? `,estimated_cost: { _gte: "${Number(params.estimatedCostMin.replaceAll(',', ''))}",  _lte: "${Number(
                  params.estimatedCostMax.replaceAll(',', '')
                )}"}`
              : ''
          );
        if (params.submissionDate?.length) {
          const startDate = params.submissionDate[0] && new Date(params.submissionDate[0]).toISOString();
          const endDate = params.submissionDate[1] && new Date(params.submissionDate[1]).toISOString();

          if (startDate && endDate) {
            str = str.concat(`
              ,da_submission_date: {_gte: "${startDate}", _lte: "${endDate}"}
            `);
          } else if (startDate) {
            str = str.concat(`
              ,da_submission_date: {_gte: "${startDate}"}
            `);
          } else if (endDate) {
            str = str.concat(`
              ,da_submission_date: {_lte: "${endDate}"}
            `);
          }
        }

        let pageIndex = 0;
        if (params?.pageIndex) pageIndex = `offset: ${params.pageIndex * 50}`;
        else pageIndex = `offset: 0`;
        return `{ ${str}}, ${pageIndex},limit : 50 `;
      }
    }),
    insertConstructionProjectNoFollow: insert(
      builder,
      'project_no_follow',
      constructionProjectNoFollowType,
      afterInsert('removeConstructionProject')
    ),
    removeConstructionProject: builder.mutation({
      query: (project) => ({
        body: gql`
        mutation {
          insert_project_no_follow(objects: {source: "${project.source}", source_id: "${
          project.da_id ? project.da_id : project.tender_id
        }"}) {
            affected_rows
          }
          delete_project(where: {id: {_eq: ${project.id}}}) 
          {affected_rows}
        }`
      }),
      onQueryStarted: afterInsert('getConstructionProjects')
    }),
    updateConstructionProject: builder.mutation({
      query: (project) => {
        project = escapeBackslashes(project);
        return ({
          body: gql`
          mutation {
            delete_project_contact(where: {project_id: {_eq: ${project.id}}})
            {affected_rows}
            ${
              project.contacts && project.contacts.length > 0
                ? `insert_project_contact(
              objects: ${objectToStr(
                project.contacts.map((s) => ({
                  name: s.name && s.name !== 'undefined' && s.name !== '' ? s.name.replace(/"|'/g, '') : null,
                  address:
                    s.address && s.address !== 'undefined' && s.address !== '' ? s.address.replace(/"|'/g, '') : null,
                  email: s.email && s.email !== 'undefined' && s.email !== '' ? s.email.replace(/"|'/g, '') : null,
                  website:
                    s.website && s.website !== 'undefined' && s.website !== '' ? s.website.replace(/"|'/g, '') : null,
                  type: s.type && s.type !== 'undefined' && s.type !== '' ? s.type.replace(/"|'/g, '') : null,
                  phone: s.phone && s.phone !== 'undefined' && s.phone !== '' ? s.phone.replace(/"|'/g, '') : null,
                  company:
                    s.company && s.company !== 'undefined' && s.company !== '' ? s.company.replace(/"|'/g, '') : null,
                  source: s.source,
                  project_id: project.id
                }))
              )}, 
            )  {affected_rows}`
                : ''
            }
            delete_project_sector(where: {project_id: {_eq: ${project.id}}})
            {affected_rows}                
            ${
              project.sectors && project.sectors.length > 0
                ? `insert_project_sector(
              objects: ${objectToStr(
                project.sectors.map((s) => ({
                  name: s,
                  project_id: project.id
                }))
              )}, 
            )  {affected_rows}`
                : ''
            }            
            delete_project_resource(where: {project_id: {_eq: ${project.id}}})
            {affected_rows}
            insert_project_resource(
              objects: ${objectToStr(
                project.resources.map((s) => ({
                  name: s.name,
                  type: s.type,
                  url: s.url,
                  project_id: project.id
                }))
              )}, 
            )  {affected_rows}          
            update_project_by_pk (pk_columns: {id: ${project.id}}, _set: {
              appointment_method: "${
                project.appointmentMethod.match(/null/)
                  ? (project.appointmentMethod = null)
                  : project.appointmentMethod.replace(/"|&|:/g, '')
              }",
              category: "${project.category.replace(/"|&|:/g, '')}",
              construction_end_date: ${project.constructionEnd ? '"'.concat(project.constructionEnd).concat('"') : null},
              construction_start_date: ${
                project.constructionStart ? '"'.concat(project.constructionStart).concat('"') : null
              },
              contract_type: "${project.contractType.replace(/"|&|:/g, '')}", 
              council: "${project.council.replace(/"|&|:/g, '')}",
              suburb: "${project.suburb.replace(/"|&|:/g, '')}",
              country: "${project.country.replace(/"|&|:/g, '')}",
              created_at: "${project.createdAt}",             
              da_end_date: ${project.daEnd ? '"'.concat(project.daEnd).concat('"') : null},
              da_id: "${project.daId}",
              da_submission_date: ${project.daSubmission ? '"'.concat(project.daSubmission).concat('"') : null},
              description: "${project.description.replace(/"|&|:/g, '')}",
              development_type: "${project.developmentType.replace(/"|&/g, '')}",
              floor_area: ${project.floorArea},
              cost: ${project.cost === 0 ? 0 : Number(project.cost)}, 
              estimated_cost: ${project.estimatedCost === 0 ? 0 : Number(project.estimatedCost)},
              green_building: "${project.greenBuilding}",
              ownership_type: "${project.ownershipType}",
              address: "${project.address.replace(/"|&/g, '').replace(/\/n/g, ' ')}",
              postcode: "${project.postcode}",
              region: "${project.region.replace(/"|&/g, '')}",
              site_area: ${project.siteArea},
              stage: "${project.stage.replace(/"|&/g, '')}",
              state: "${project.state.replace(/"|&/g, '')}",
              status: "${project.status.replace(/"|&|:/g, '')}",
              sub_category: "${project.subCategory.replace(/"|&/g, '')}",
              summary: "${project.summary.replace(/"|&/g, '')}",
              tender_end_date: ${project.tenderEnd ? '"'.concat(project.tenderEnd).concat('"') : null},
              tender_id: "${project.tenderId}",
              tender_submission_date: ${
                project.tenderSubmission ? '"'.concat(project.tenderSubmission).concat('"') : null
              },
              title: "${project.title.replace(/"|&|:/g, '').replace(/\/n/g, ' ')}",
              review_status: "${project.reviewStatus}"    
              
            })  ${constructionProjectType}
          }        `
        })
      },
      transformResponse: (response) => response.update_project_by_pk,
      invalidatesTags: () => ['ConstructionProject'],
      onQueryStarted: afterUpdate('getConstructionProjects')
    }),
    getConstructionProjectUpdate: select(builder, 'project_update', constructionProjectUpdateType, false, {
      cacheKey: 'getConstructionProjectUpdate(undefined)',
      filter: (params) => `{ project_id: { _eq: ${params.id}}}`,
      order: `{updated_at: desc}`
    }),
    removeConstructionProjectUpdate: builder.mutation({
      query: (project) => ({
        body: gql`
        mutation {
          delete_project_update(where: {project_id: {_eq: ${project.id}}}) 
          {affected_rows}
        }`
      })
    }),
    getConstructionProjectProjectProfile: select(builder, 'project_profile', projectProfileType, false, {
      cacheKey: 'getProjectProfile(undefined)',
      filter: (params) => `{profile_id: {_eq: ${params.profile_id}}}`
    }),
    updateConstructionProjectProjectProfile: update(
      builder,
      'project_profile',
      projectProfileType,
      afterUpdate('getConstructionProjectProjectProfile')
    ),
    insertConstructionProjectProjectProfile: insert(
      builder,
      'project_profile',
      projectProfileType,
      afterInsert('getConstructionProjectProjectProfile')
    ),
    removeConstructionProjectProjectProfile: remove(
      builder,
      'project_profile',
      projectProfileType,
      afterRemove('getConstructionProjectProjectProfile')
    )
  }),
  overrideExisting: false,
});

export const { 
  useGetConstructionProjectsQuery,
  useInsertConstructionProjectNoFollowMutation,
  useRemoveConstructionProjectMutation,
  useUpdateConstructionProjectMutation,
  useGetConstructionProjectUpdateQuery,
  useRemoveConstructionProjectUpdateMutation,
  useGetConstructionProjectProjectProfileQuery,
  useInsertConstructionProjectProjectProfileMutation,
  useUpdateConstructionProjectProjectProfileMutation,
  useRemoveConstructionProjectProjectProfileMutation,
} = projectApi